import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SN_SCT_TAX_REG_001_POP_01 from "@menu/SCT/SN_SCT_TAX_REG_001/SN_SCT_TAX_REG_001_POP_01";
import SN_SCT_TAX_REG_001_POP_02 from "@menu/SCT/SN_SCT_TAX_REG_001/SN_SCT_TAX_REG_001_POP_02";
import SN_SCT_TAX_REG_001_POP_03 from "@menu/SCT/SN_SCT_TAX_REG_001/SN_SCT_TAX_REG_001_POP_03";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

// 외주매입등록
const SN_SCT_TAX_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sct_tax_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    try {
      const insertList = gridRefs.current["grid1"].current.getChange().insert;
      if (insertList.length > 0) {
        showDialog("alter", "수정중인 건이 있습니다.");
        return false;
      }

      gridRefs.current["grid1"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowH = (): any => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_biz,
        no_cls: "",
        dt_cls: today,
        fg_sct: "SCT",
        cd_dept: userInfo?.cd_dept,
        cd_emp: userInfo?.cd_emp,
        cd_gp: userInfo?.cd_gp,
        nm_emp: userInfo?.nm_emp,
        yn_autoprocess: "N",
        cd_exch: "top1",
        rt_exch: 1,
        fg_trns: "1",
        fg_vat: "21",
        cd_fin: "001",
        yn_docu: "N",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      return gridRefs.current["grid2"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (): any => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      return {
        cd_firm: userInfo?.cd_firm,
        cd_biz: userInfo?.cd_firm,
        no_cls: focusedNodeH.data?.no_cls,
        no_line: maxNoLine + 1,
        // no_line_rcv: maxNoLine + 1,
        // no_line_po: maxNoLine + 1,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    const rowCount = gridRefs.current["grid2"].current.getRowCount();
    if (rowCount === 0) return;

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    onAfterSetFooter();

    if (rowCount === 1) {
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      gridRefs.current["grid1"].current.clearBlock(focusedRowNodeH);
    }
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const taxHChanges = gridRefs.current["grid1"].current.getChange();
      const taxLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = taxHChanges.insert ?? [];
      const lineInserts = taxLChanges.insert ?? [];
      if (
        taxHChanges.modifiedRowcount === 0 &&
        taxLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (headerInserts.length !== 0) {
        const noSlip = await applySlip({
          cd_column: "no_cls",
          cd_module: Menuprops.cd_module,
          no_line: 4,
          cd_prefix: "SCT",
          dt: headerInserts[0].dt_cls,
          girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
          inserts: [taxHChanges, taxLChanges],
          cd_menu: Menuprops.cd_menu,
        });
      }

      const saveData = {
        taxh: taxHChanges,
        taxl: taxLChanges,
      };
      const ok = await fnSave("SN_SCT_TAX_REG_001_SAVE_01", saveData);
      if (!ok) {
        gridRefs.current["grid1"].current.resetUpdate(true);
        gridRefs.current["grid2"].current.resetUpdate(true);
        return false;
      }

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid1BtnClick = async (id: string) => {
    if (id == "add_store") {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH && focusedNodeH.data.state !== "insert") {
        await showDialog("alter", "신규 행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz || focusedNodeH.data.cd_biz,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        cd_cust: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.cd_cust
          : selectedFormData.cd_cust,
        nm_cust_sort: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.nm_cust_sort
          : selectedFormData.nm_cust_sort,
      };

      if (focusedNodeH.data.fg_sct === "PRD") {
        await showDialog(
          "alter",
          "공정외주인 경우 '공정외주입고적용'만 가능합니다"
        );
        return;
      }

      const component = (
        <SN_SCT_TAX_REG_001_POP_01 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "외주입고적용",
        children: component,
        // width: "70%",
        validation: true,
      });
      if (!result || result === "close") return;

      let headerResult: any[] = result[0];
      let lineResult: any[] = result[1];

      if (focusedNodeH && headerResult[0]) {
        // for (const key in headerResult[0]) {
        //   focusedNodeH.setDataValue(key, headerResult[0][key], "noEvent");
        // }

        // focusedNodeH.setDataValue("cd_biz", headerResult[0].cd_biz, "noEvent");
        focusedNodeH.setDataValue(
          "cd_cust",
          headerResult[0].cd_cust,
          "noEvent"
        );
        focusedNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort,
          "noEvent"
        );
        focusedNodeH.setDataValue(
          "fg_trns",
          headerResult[0].fg_trns,
          "noEvent"
        );
        focusedNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        focusedNodeH.setDataValue("cd_emp", headerResult[0].cd_emp, "noEvent");
        focusedNodeH.setDataValue("cd_gp", headerResult[0].cd_gp, "noEvent");
        // focusedNodeH.setDataValue("cd_dept", headerResult[0].cd_dept, "noEvent");
        // focusedNodeH.setDataValue("nm_emp", headerResult[0].nm_emp, "noEvent");
        // focusedNodeH.setDataValue("dc_rmk", headerResult[0].rt_exch, "noEvent");
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            // for (const key in result) {
            //   addedNode.setDataValue(key, result[key], "noEvent");
            // }
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("am_k", result.am_k, "noEvent");
            addedNode.setDataValue("am_vat", result.am_vat, "noEvent");
            addedNode.setDataValue("am_supply", result.am_supply, "noEvent");
            addedNode.setDataValue("um_supply", result.um_supply, "noEvent");
            addedNode.setDataValue("no_rcv", result.no_io, "noEvent");
            addedNode.setDataValue("no_line_rcv", result.no_line, "noEvent");
            addedNode.setDataValue("no_po", result.no_order, "noEvent");
            addedNode.setDataValue(
              "no_line_po",
              result.no_line_order,
              "noEvent"
            );
            addedNode.setDataValue("fg_io", result.fg_io, "noEvent");
            addedNode.setDataValue("fg_vat", result.fg_vat, "noEvent");
            addedNode.setDataValue("rt_exch", result.rt_exch, "noEvent");
            addedNode.setDataValue("cd_gp", result.cd_gp, "noEvent");
            addedNode.setDataValue("dt_dlv", result.dt_dlv, "noEvent");
            addedNode.setDataValue("no_wbs", result.no_wbs, "noEvent");
            addedNode.setDataValue("no_cbs", result.no_cbs, "noEvent");
            addedNode.setDataValue("no_prq", result.no_prq, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.setDataValue("fg_rcv", result.fg_io, "noEvent");
          }
        }
      });
      gridRefs.current["grid1"].current.applyBlock(focusedNodeH);
      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
    if (id == "add_pro_store") {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH && focusedNodeH.data.state !== "insert") {
        await showDialog("alter", "신규 행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz || focusedNodeH.data.cd_biz,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        cd_cust: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.cd_cust
          : selectedFormData.cd_cust,
        nm_cust_sort: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.nm_cust_sort
          : selectedFormData.nm_cust_sort,
      };

      if (focusedNodeH.data.fg_sct === "SCT") {
        await showDialog("alter", "외주발주 인 경우 '입고적용'만 가능합니다");
        return;
      }

      const component = (
        <SN_SCT_TAX_REG_001_POP_02 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "공정외주입고적용",
        children: component,
        // width: "70%",
        validation: true,
      });
      if (!result || result === "close") return;

      let headerResult: any[] = result[0];
      let lineResult: any[] = result[1];

      if (focusedNodeH && headerResult[0]) {
        focusedNodeH.setDataValue("cd_biz", headerResult[0].cd_biz, "noEvent");
        focusedNodeH.setDataValue(
          "cd_cust",
          headerResult[0].cd_cust,
          "noEvent"
        );
        focusedNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort,
          "noEvent"
        );
        focusedNodeH.setDataValue(
          "fg_trns",
          headerResult[0].fg_trns,
          "noEvent"
        );
        focusedNodeH.setDataValue("cd_emp", headerResult[0].cd_emp, "noEvent");
        focusedNodeH.setDataValue("cd_gp", headerResult[0].cd_gp, "noEvent");
        focusedNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        // focusedNodeH.setDataValue("cd_dept", headerResult[0].cd_dept, "noEvent");
        // focusedNodeH.setDataValue("nm_emp", headerResult[0].nm_emp, "noEvent");
        // focusedNodeH.setDataValue("dc_rmk", headerResult[0].rt_exch, "noEvent");
      }
      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("qt", result.qt, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("am_k", result.am_k, "noEvent");
            addedNode.setDataValue("am_vat", result.am_vat, "noEvent");
            addedNode.setDataValue("am_supply", result.am_supply, "noEvent");
            addedNode.setDataValue("um_supply", result.um_supply, "noEvent");
            addedNode.setDataValue("no_rcv", result.no_io, "noEvent");
            addedNode.setDataValue("no_line_rcv", result.no_line, "noEvent");
            addedNode.setDataValue("no_po", result.no_order, "noEvent");
            addedNode.setDataValue(
              "no_line_po",
              result.no_line_order,
              "noEvent"
            );
            addedNode.setDataValue("fg_io", result.fg_io, "noEvent");
            addedNode.setDataValue("fg_vat", result.fg_vat, "noEvent");
            addedNode.setDataValue("rt_exch", result.rt_exch, "noEvent");
            addedNode.setDataValue("cd_gp", result.cd_gp, "noEvent");
            addedNode.setDataValue("dt_dlv", result.dt_dlv, "noEvent");
            addedNode.setDataValue("no_wbs", result.no_wbs, "noEvent");
            addedNode.setDataValue("no_cbs", result.no_cbs, "noEvent");
            addedNode.setDataValue("no_prq", result.no_prq, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.setDataValue("fg_rcv", result.fg_io, "noEvent");
          }
        }
      });
      gridRefs.current["grid1"].current.applyBlock(focusedNodeH);
      await CalAmBulk(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "rt_exch"
      );
    }
    if (id === "btnSctResult") {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH && focusedNodeH.data.state !== "insert") {
        await showDialog("alter", "신규 행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz || focusedNodeH.data.cd_biz,
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
        cd_cust: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.cd_cust
          : selectedFormData.cd_cust,
        nm_cust_sort: focusedNodeH.data.cd_cust
          ? focusedNodeH.data.nm_cust_sort
          : selectedFormData.nm_cust_sort,
      };

      const component = (
        <SN_SCT_TAX_REG_001_POP_03 selectedData={selectedData} />
      );
      const result = await showResizablePopupModal({
        title: "외주실적적용",
        children: component,
        validation: true,
        width: "1350",
        height: "720",
      });
      if (!result || result === "close") return;

      const lineResult: any[] = result[0];
      if (focusedNodeH && lineResult[0]) {
        focusedNodeH.setDataValue("cd_biz", lineResult[0].cd_biz, "noEvent");
        focusedNodeH.setDataValue("fg_sct", "PRD", "noEvent");
        focusedNodeH.setDataValue("cd_cust", lineResult[0].cd_cust, "noEvent");
        focusedNodeH.setDataValue(
          "nm_cust_sort",
          lineResult[0].nm_cust_sort,
          "noEvent"
        );
      }
      lineResult.map((result: any) => {
        if (lineResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            addedNode.setDataValue("fg_rcv", "1", "noEvent");
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("qt", result.qt, "noEvent");
            addedNode.setDataValue("um", result.um, "noEvent");
            addedNode.setDataValue("am", result.am, "noEvent");
            addedNode.setDataValue("no_rcv", result.no_work, "noEvent");
            addedNode.setDataValue("no_line_rcv", result.no_line, "noEvent");
            addedNode.setDataValue("no_po", result.no_po, "noEvent");
            addedNode.setDataValue("dc_unit", result.dc_unit, "noEvent");
            addedNode.setDataValue("no_wo", result.no_wo, "noEvent");
            addedNode.setDataValue("no_line_wo", result.no_line_wo, "noEvent");
            addedNode.setDataValue("cd_var2", result.cd_var2, "noEvent");
          }
        }
      });
      gridRefs.current["grid1"].current.applyBlock(focusedNodeH);
      // await CalAmBulk(
      //   gridRefs.current["grid1"],
      //   gridRefs.current["grid2"],
      //   "rt_exch"
      // );
    }
  };

  const onGrid2BtnClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onFocusedRowChanged = async (event: any) => {
    const selectedRow = event;
    if (selectedRow) {
      const no_cls = selectedRow.no_cls;
      setSelectedDocu(no_cls);
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    await CalAmBulk(
      gridRefs.current["grid1"],
      gridRefs.current["grid2"],
      colId
    );
  };

  const onCellValueChanged = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onAfterSetFooter = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onFocusedRowChanged: onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onBtnEtcClick: onGrid1BtnClick,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChanged,
          onBtnDeleteClick: onGrid2BtnClick,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "매입정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_cls"] }}
            header={
              <div className="relative flex space-x-4">
                {config?.MAS8025?.dc_rmk === "TEX" && (
                  <div>
                    <SnButton
                      text={"외주실적적용"}
                      onClick={() => onGrid1BtnClick("btnSctResult")}
                    />
                  </div>
                )}
                {config?.MAS8025?.dc_rmk !== "TEX" && (
                  <>
                    <div>
                      <SnButton
                        text={"입고적용"}
                        onClick={() => onGrid1BtnClick("add_store")}
                      />
                    </div>

                    {config?.SCT0010?.dc_ctrl === "Y" && (
                      <div style={{ width: 140 }}>
                        <SnButton
                          text={"공정외주입고적용"}
                          onClick={() => {
                            onGrid1BtnClick("add_pro_store");
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SCT_TAX_REG_001;
